import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type DateFilter from 'teacher-ui-client/services/date-filter'
import type SessionService from 'teacher-ui-client/services/session'
import type RouterService from '@ember/routing/router-service'
import type Transition from '@ember/routing/transition'
import type Store from '@ember-data/store'
import type Teacher from 'teacher-ui-client/models/teacher'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type School from 'teacher-ui-client/models/school'
import type Student from 'teacher-ui-client/models/student'
import { unionBy } from 'lodash'
import type { Log } from '@blakeelearning/log'
import { hasId } from 'teacher-ui-client/utils/type-guards'

/**
 * Represents ui_scope_name dynamic segment in reporting URLs
 */
export type UIScopeName = 'school' | 'teacher' | 'school-class'

/**
 * Represents ui_scope_name and ui_scope_id dynamic segments in reporting URLs.
 * Can be applied to the gravity data object below to "scope" it to return data only relevant to that scope.
 */
export class UIScope {
  id: string

  scope: UIScopeName

  constructor(scope: UIScopeName, id: string) {
    this.id = id
    this.scope = scope
  }
}

type SchoolWithId = School & { id: string }
type TeacherWithId = Teacher & { id: string }
type SchoolClassWithId = SchoolClass & { id: string }
type StudentWithId = Student & { id: string }

/**
 * Returns an array of valid scopes that can be used. For a subco, they are allowed school scope, and all teacher &
 * classes scopes. A teacher is only allowed their own teacher scope and their own school classes.
 * [
 *   { scope: 'school', id: '1' },
 *   { scope: 'teacher', id: '1' },
 *   { scope: 'teacher', id: '2' },
 *   { scope: 'teacher', id: '3' },
 *   { scope: 'school-class', id: '1' },
 *   { scope: 'school-class', id: '2' },
 *   { scope: 'school-class', id: '3' },
 * ]
 *
 * First one in the array will be "default" scope that the user is returned to if they're attempting an invalid scope.
 */
export function validUiScopesFor(school: School, teacher: Teacher, subscriptionType: SubscriptionType): UIScope[] {
  const isSubco = teacher.subcoFor(subscriptionType)
  if (isSubco && school.id) {
    const teachers = getTeachersFromSchools([school], subscriptionType)
    const schoolClasses = getSchoolClassesFromTeachers(teachers, subscriptionType)
    return [
      // subco allowed to visit school scope
      new UIScope('school', school.id),
      // subco can visit all teacher scopes within school
      ...teachers.map((teacher) => new UIScope('teacher', teacher.id)),
      // subco can visit all school-class scopes of those teachers
      ...schoolClasses.map((schoolClass) => new UIScope('school-class', schoolClass.id)),
    ]
  }

  if (!teacher.id) return []

  const teachers = [teacher]
  const schoolClasses = getSchoolClassesFromTeachers(teachers, subscriptionType)
  return [
    // current teacher scope
    new UIScope('teacher', teacher.id),
    // teacher can visit all their own school-class scopes
    ...schoolClasses.map((schoolClass) => new UIScope('school-class', schoolClass.id)),
  ]
}

function getTeachersFromSchools(schools: School[], subscriptionType: SubscriptionType): TeacherWithId[] {
  const matchesSubType = (item: { subscriptionType: SubscriptionType }) => item.subscriptionType === subscriptionType
  return schools.reduce<TeacherWithId[]>((acc, school) => {
    // only include teachers that have at least 1 class with matching subscriptiontype
    const schoolTeachers = school.teachers?.filter((t) => t.schoolClasses.some(matchesSubType)).filter(hasId) ?? []
    return unionBy(acc, schoolTeachers, (i) => i.id)
  }, [])
}

function getSchoolClassesFromTeachers(teachers: Teacher[], subscriptionType: SubscriptionType): SchoolClassWithId[] {
  const matchesSubType = (item: { subscriptionType: SubscriptionType }) => item.subscriptionType === subscriptionType

  return teachers.reduce<SchoolClassWithId[]>((acc, t) => {
    // school class much have matching subscriptionType to be included
    const teacherClasses = t.schoolClasses.filter(matchesSubType).filter(hasId)
    return unionBy(acc, teacherClasses, (i) => i.id)
  }, [])
}

function getStudentsFromSchoolClasses(schoolClasses: SchoolClass[]): StudentWithId[] {
  return schoolClasses.reduce<StudentWithId[]>((acc, sc) => unionBy(acc, sc.students?.filter(hasId), (i) => i.id), [])
}

function defaultProductSlugForSubscriptionType(subscriptionType: SubscriptionType) {
  switch (subscriptionType) {
    case SubscriptionType.Reading:
      return 're'
    case SubscriptionType.Maths:
      return 'maths'
    case SubscriptionType.Writing:
      return 'writing-legends'
  }
}

interface Params extends Record<string, unknown> {
  ui_scope_name: UIScope['scope']
  ui_scope_id: UIScope['id']
}

export default class extends Route {
  @service dateFilter!: DateFilter

  @service session!: SessionService

  @service router!: RouterService

  @service store!: Store

  @service log!: Log

  queryParams = {
    dateRange: {
      refreshModel: true,
      replace: true,
    },
  }

  beforeModel(transition: Transition) {
    // transition queryParams dateRange is undefined if no value set yet (it doesnt fallback to the controller value).
    const dateRange = transition.to?.queryParams.dateRange
    if (typeof dateRange === 'string' && dateRange !== '') {
      this.dateFilter.dateRange = dateRange
    } else {
      this.dateFilter.dateRange = this.dateFilter.toDateRange(this.dateFilter.thisAcademicPeriod)
    }

    /**
     * Guard against incorrect scopes for current user.
     * This can happen for multiple reasons;
     * - user modifies their url (eg teacher tries to view school scope?)
     * - the rails links to reporting pages currently use incorrect scope (they show the school link even for a teacher)
     *
     * If their scope is not correct, transition to an appropriate scope.
     */
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    const { ui_scope_name: uiScopeName, ui_scope_id: uiScopeId } = this.paramsFor('reporting.ui-scope') as Params

    // Determine the valid scopes that this user can visit (subco vs teacher)
    const validUiScopes = validUiScopesFor(this.session.currentSchool, this.session.currentTeacher, subscriptionType)

    // Check that there are students, regardless of which scope the user might be visiting.
    // It might be a new school or teacher with no students yet
    const hasStudents = validUiScopes
      .filter(({ scope }) => scope === 'school-class')
      .some(({ id }) => this.store.peekRecord<SchoolClass>('school-class', id)?.students.length)
    if (!hasStudents) {
      void this.router.replaceWith('subscription-type.error-no-students')
      return undefined
    }

    // Determine if the scope the user is visiting is allowed/valid for their user type (subco vs teacher)
    const isValidScope = validUiScopes.some(({ scope, id }) => uiScopeName === scope && uiScopeId === id)

    /* If not a valid scope, redirect them to a report for their desired target product, with a valid scope.
     * This functionality is currently relied on by rails reports links, as they generate a link with an invalid scope.
     *
     * When we no longer rely on this for rails links, consider redirecting to a simple error/404 page instead.
     */
    if (!isValidScope) {
      // determine what product they're going to, based on the transition
      const desiredProductSlug =
        transition.to?.name
          .split('.')
          .find((slug) =>
            ['re', 'rex', 'fast-phonics', 'maths', 'mathseeds-prime', 'writing-legends'].includes(slug),
          ) ?? defaultProductSlugForSubscriptionType(subscriptionType)

      // from the desired product slug, redirect to a report for that product, with a valid scope
      // Use the first scope from the valid scopes array as the scope to go to.
      void this.redirectToReportForProductSlug(desiredProductSlug, validUiScopes[0])
      return undefined
    }
  }

  model(params: Params, _transition: Transition) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    const { ui_scope_name: uiScopeName, ui_scope_id: uiScopeId } = params

    /* Apply the uiScope (from the route dynamic segments) to the gravity data objects. This makes various functions in
     * the object return appropriately scoped gravity data.
     */
    const uiScope = new UIScope(uiScopeName, uiScopeId)

    /**
     * Build up a scopedData object that contains all the records relevant to the selected ui scope.
     */
    let schools: SchoolWithId[] = []
    let teachers: TeacherWithId[] = []
    let schoolClasses: SchoolClassWithId[] = []
    let students: StudentWithId[] = []

    if (uiScope.scope === 'school') {
      const peekedRecord = this.store.peekRecord<School>('school', uiScope.id)
      schools = peekedRecord && hasId(peekedRecord) ? [peekedRecord] : []

      teachers = getTeachersFromSchools(schools, subscriptionType)
      schoolClasses = getSchoolClassesFromTeachers(teachers, subscriptionType)
      students = getStudentsFromSchoolClasses(schoolClasses)
    } else if (uiScope.scope === 'teacher') {
      schools = []
      const peekedRecord = this.store.peekRecord<Teacher>('teacher', uiScope.id)
      teachers = peekedRecord && hasId(peekedRecord) ? [peekedRecord] : []

      schoolClasses = getSchoolClassesFromTeachers(teachers, subscriptionType)
      students = getStudentsFromSchoolClasses(schoolClasses)
    } else if (uiScope.scope === 'school-class') {
      schools = []
      teachers = []
      const peekedRecord = this.store.peekRecord<SchoolClass>('school-class', uiScope.id)
      schoolClasses = peekedRecord && hasId(peekedRecord) ? [peekedRecord] : []
      students = getStudentsFromSchoolClasses(schoolClasses)
    }

    const scopedData = { schools, teachers, schoolClasses, students }

    return {
      uiScope,
      scopedData,
      period: this.dateFilter.period,
    }
  }

  redirectToReportForProductSlug(productSlug: string, validScope: UIScope) {
    switch (productSlug) {
      case 're':
        return this.router.replaceWith('reporting.ui-scope.re.dashboard', validScope.scope, validScope.id)
      case 'rex':
        return this.router.replaceWith('reporting.ui-scope.rex.dashboard', validScope.scope, validScope.id)
      case 'fast-phonics':
        return this.router.replaceWith('reporting.ui-scope.fast-phonics.dashboard', validScope.scope, validScope.id)
      case 'maths':
        return this.router.replaceWith('reporting.ui-scope.maths.dashboard', validScope.scope, validScope.id)
      case 'mathseeds-prime':
        return this.router.replaceWith(
          'reporting.ui-scope.mathseeds-prime.dashboard-table',
          validScope.scope,
          validScope.id,
        )
      case 'writing-legends':
        return this.router.replaceWith(
          'reporting.ui-scope.writing-legends.dashboard-table',
          validScope.scope,
          validScope.id,
        )
      default:
        this.log.error('invalid scope provided and unable to redirect to suitable report')
    }
  }
}
